/**
 * Auth context — exposes the current Supabase session/user to the app
 * and refreshes automatically when auth state changes.
 *
 * Wrap the app in <AuthProvider> once in main.tsx, then use useAuth()
 * anywhere to read { user, session, loading, signOut }.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Read the existing session on mount (cached in localStorage by Supabase SDK).
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    }).catch(() => {
      // Session restore failed (e.g. storage unavailable) — treat as signed out
      // rather than leaving the app stuck on the loading splash forever.
      setSession(null);
      setLoading(false);
    });

    // Subscribe to future auth state changes (sign in, sign out, token refresh).
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(false);
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      loading,
      signOut: async () => {
        await supabase.auth.signOut();
        // Belt-and-suspenders: tell the service worker to drop any legacy
        // authenticated-API cache so the next user on a shared device can't be
        // served the prior session's data (see apps/web/src/sw.ts, QA P1-16).
        navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
      },
    }),
    [session, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
